import React, { useState } from 'react';
import { useVeo } from '../hooks/useVeo';
import { Spinner } from './Spinner';

export const VideoGenerator = ({ uploadedFile }) => {
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [prompt, setPrompt] = useState('A dynamic 8-second product showcase video of this item. Use a slow 360-degree spin or a smooth panning shot with a clean, professional background.');
  const { 
    generateVideo, 
    isGenerating, 
    videoUrl, 
    statusMessage, 
    error,
    promptForApiKey,
    selectApiKey,
  } = useVeo();

  const handleGenerateClick = () => {
     if (!prompt.trim()) {
        alert("Please enter a prompt for the video.");
        return;
    }
    generateVideo(uploadedFile, aspectRatio, prompt);
  };

  return (
    <div className="bg-[#270b79] bg-opacity-50 p-6 rounded-2xl shadow-2xl border border-[#F06B32] border-opacity-30">
      <div className="flex flex-col gap-6 mb-8">
        <div>
            <label htmlFor="video-prompt" className="block font-semibold text-white mb-3 text-lg">Video Generation Prompt:</label>
            <textarea
                id="video-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., A dramatic slow-motion video of the product with water splashing effects and cinematic lighting..."
                className="w-full bg-[#1a074f] border-2 border-[#270b79] text-white text-sm rounded-xl focus:ring-2 focus:ring-[#F06B32] focus:border-[#F06B32] block p-4 min-h-[100px] transition-all duration-300"
                disabled={isGenerating}
            />
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4 bg-[#1a074f] p-4 rounded-xl">
            <label htmlFor="aspectRatio" className="font-semibold text-white text-sm uppercase tracking-wide">Video Format:</label>
            <select
            id="aspectRatio"
            value={aspectRatio}
            onChange={(e) => setAspectRatio(e.target.value)}
            className="bg-[#270b79] border-2 border-[#270b79] text-white text-sm rounded-lg focus:ring-2 focus:ring-[#F06B32] focus:border-[#F06B32] block w-full sm:w-auto p-3 transition-all duration-300"
            disabled={isGenerating}
            >
            <option value="16:9">16:9 (Landscape)</option>
            <option value="9:16">9:16 (Portrait)</option>
            </select>
            <button
            onClick={handleGenerateClick}
            disabled={isGenerating || promptForApiKey}
            className="w-full sm:w-auto bg-[#F06B32] hover:bg-[#e55a27] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center shadow-lg"
            >
            {isGenerating && <Spinner className="w-5 h-5 mr-2" />}
            {isGenerating ? 'Creating Video...' : 'Generate Video'}
            </button>
        </div>
       </div>

      {promptForApiKey && (
        <div className="bg-yellow-900 bg-opacity-70 border border-yellow-600 text-yellow-200 px-6 py-4 rounded-xl relative mb-6 shadow-lg" role="alert">
          <strong className="font-bold text-lg">🔑 Action Required:</strong>
          <span className="block mt-2"> Please select a Google Cloud API key to proceed with video generation. </span>
          <p className="text-sm mt-3">For more info on billing, visit <a href="https://ai.google.dev/gemini-api/docs/billing" target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-100">ai.google.dev/gemini-api/docs/billing</a>.</p>
          <button
            onClick={selectApiKey}
            className="mt-4 bg-[#F06B32] hover:bg-[#e55a27] text-white font-bold py-3 px-6 rounded-lg transition-all duration-300"
          >
            Select API Key
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-900 bg-opacity-50 border border-red-700 rounded-xl">
          <p className="text-red-300 text-center">{error}</p>
        </div>
      )}
      
      <div className="aspect-video bg-[#1a074f] rounded-xl flex items-center justify-center relative overflow-hidden border-2 border-[#270b79]">
        {isGenerating && (
          <div className="text-center p-8">
            <Spinner className="w-16 h-16 text-[#F06B32] mx-auto mb-4" />
            <p className="text-white font-semibold text-lg mb-2">{statusMessage}</p>
            <p className="text-gray-400 text-sm">This may take a few minutes...</p>
          </div>
        )}
        {videoUrl && !isGenerating && (
          <>
            <video src={videoUrl} controls autoPlay loop className="w-full h-full object-contain rounded-lg" />
            <a
                href={videoUrl}
                download="ai-generated-video.mp4"
                className="absolute bottom-6 right-6 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 shadow-lg"
            >
                ⬇️ Download Video
            </a>
          </>
        )}
        {!isGenerating && !videoUrl && (
          <div className="text-center text-gray-400 p-8">
            <div className="w-20 h-20 bg-[#270b79] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-[#F06B32]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Ready to Create Amazing Videos</h3>
            <p className="text-gray-300">Your AI-generated product video will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
};